import type {EventFilterValue, GPEventResponse, GPGamePlayerResponse} from "../../types";
import {PlayerStatCalculationResult, PlayerStatType} from "./definition";
import {createStatTypeConditions} from "../Video/Filter/conditionFactory";
import EventFilter from "../Video/Filter/eventFilter";
import {createStatTypeFilterValues} from "../Video/Filter/filterFactory";
import {_} from "i18n";
import type {Keys} from "i18n/locales/types";
import { excludeDirectionalEvents } from "../../functions";
import { ProcessingLevelEnum } from "../../types";

const filterEvents = (eventFilter: EventFilter, statType: PlayerStatType, periods: string[], gamePlayerId: number | null): GPEventResponse[] => {
	const conditions = createStatTypeConditions(statType);

	if (conditions.length === 0) {
		return [];
	}

	conditions.push({ type: "period", value: periods });

	if (gamePlayerId) {
		conditions.push({ type: "player", value: [gamePlayerId.toString()] });
	}

	return eventFilter.filterByConditions(conditions, eventFilter.events);
}

export function calculatePlayerStats(eventFilter: EventFilter, statType: PlayerStatType, player: GPGamePlayerResponse, periods: string[], gameProcessingLevel: ProcessingLevelEnum | null = null): PlayerStatCalculationResult {
	const result = {
		url: player.playerUrl,
		playerId: player.id,
		statType: statType,
		label: "",
		value: 0,
		valueFormatted: "",
		valueSort: 0,
		events: [] as GPEventResponse[],
		filterValues: [] as EventFilterValue[],
		isFilterable: false,
		isUseful: false,
	} as PlayerStatCalculationResult;

	const isLightTagged = gameProcessingLevel === ProcessingLevelEnum.Limited;

	result.filterValues = createStatTypeFilterValues(statType);

	if (statType === PlayerStatType.Name) {
		result.value = player.name;
		result.valueFormatted = result.value;
		result.valueSort = result.value;
		result.isUseful = result.value !== null;
	} else if (statType === PlayerStatType.Number) {
		result.value = player.numberSign || null;
		result.valueFormatted = result.value ? String(result.value) : null;
		result.valueSort = result.value;
		result.isUseful = result.value !== null;
	} else if (statType === PlayerStatType.Position) {
		result.value = player.position;
		result.valueFormatted = result.value ? String(_(`enum.position.${result.value}.key` as keyof Keys)) : null;
		result.valueSort = result.value;
		result.isUseful = result.value !== null;
	} else if (statType === PlayerStatType.Rating) {
		result.value = player.rating ? Math.round(player.rating * 10) / 10 : 0;
		result.valueFormatted = String(result.value);
		result.valueSort = result.value;
		result.isUseful = true;
	} else if (statType === PlayerStatType.MinutesPlayed) {
		result.value = player.minutesPlayed ? Math.round(player.minutesPlayed / 60000) : 0;
		result.valueFormatted = String(result.value);
		result.valueSort = result.value;
		result.isUseful = result.value !== 0;
	} else if (statType === PlayerStatType.PassAccuracy) {
		let passTotal = filterEvents(eventFilter, PlayerStatType.Pass, periods, player.id);
		let passSuccess = filterEvents(eventFilter, PlayerStatType.PassSuccess, periods, player.id);

		if (isLightTagged) {
			passTotal = excludeDirectionalEvents(passTotal);
			passSuccess = excludeDirectionalEvents(passSuccess);
		}

		result.value = passTotal.length !== 0 ? Math.round(passSuccess.length / passTotal.length * 100) : 0;
		result.valueFormatted = String(result.value);
		result.valueSort = result.value;
		result.isUseful = passTotal.length !== 0;
		result.events = passTotal;
	} else if (statType === PlayerStatType.AerialClearance || statType === PlayerStatType.AerialClearanceSuccess) {
		const catchInterceptionType = statType === PlayerStatType.AerialClearanceSuccess ? PlayerStatType.CatchInterceptionSuccess : PlayerStatType.CatchInterception;
		let catchInterceptions = filterEvents(eventFilter, catchInterceptionType, periods, player.id);
		let punches = filterEvents(eventFilter, PlayerStatType.Punch, periods, player.id);
		if (isLightTagged) {
			catchInterceptions = excludeDirectionalEvents(catchInterceptions);
			punches = excludeDirectionalEvents(punches);
		}
		const sumEvents = [...catchInterceptions, ...punches];

		result.value = sumEvents.length;
		result.valueFormatted = String(result.value);
		result.valueSort = result.value;
		result.isUseful = sumEvents.length !== 0;
		result.events = sumEvents;
	} else if (statType.endsWith("Ratio")) {
		const totalKey = statType.substring(0, statType.length - 5);
		const successKey = totalKey + "Success";
		const totalType = Object.entries(PlayerStatType).find(([key, value]) => value === totalKey) as [string, PlayerStatType];
		const successType = Object.entries(PlayerStatType).find(([key, value]) => value === successKey) as [string, PlayerStatType];

		if (totalType !== undefined && successType !== undefined) {
			let totalEvents = filterEvents(eventFilter, totalType[1], periods, player.id);
			let successEvents = filterEvents(eventFilter, successType[1], periods, player.id);

			if (isLightTagged) {
				totalEvents = excludeDirectionalEvents(totalEvents);
				successEvents = excludeDirectionalEvents(successEvents);
			}

			result.filterValues = createStatTypeFilterValues(totalType[1]);

			result.value = 0;
			result.events = totalEvents;
			result.valueFormatted = `${successEvents.length}/${totalEvents.length}`;

			if (totalEvents.length !== 0) {
				result.value = Math.round((successEvents.length / totalEvents.length) * 100);
				result.isUseful = true;
			}

			result.valueSort = (result.value * 1000) + totalEvents.length;
		}
	} else if (statType === PlayerStatType.ShotConversion) {
		let playerShots = filterEvents(eventFilter, PlayerStatType.Shot, periods, player.id);
		let playerGoals = filterEvents(eventFilter, PlayerStatType.Goal, periods, player.id);

		if (isLightTagged) {
			playerShots = excludeDirectionalEvents(playerShots);
			playerGoals = excludeDirectionalEvents(playerGoals);
		}

		// handle division by zero
		result.value = playerShots.length > 0
			? Math.round((playerGoals.length / playerShots.length) * 100)
			: 0;

		result.valueFormatted = `${String(result.value)}%`;
		result.valueSort = result.value;
		result.isUseful = true;
		result.events = playerShots;
	} else {
		let events = filterEvents(eventFilter, statType, periods, player.id);

		if (isLightTagged) {
			events = excludeDirectionalEvents(events);
		}

		result.events = events;
		result.value = result.events.length;
		result.valueFormatted = String(result.value);
		result.valueSort = result.value;
		result.isUseful = result.events.length > 0;
	}

	result.isFilterable = result.events.length > 0 && result.filterValues.length > 0;

	periods.forEach((p) => {
		result.filterValues.push({
			key: `period.${p}`,
			value: [p],
		});
	});

	return result;
}
